import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, ShieldAlert, Users } from 'lucide-react';
import { AppLayout } from '@/components/Layout/AppLayout';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ErrorMessage } from '@/components/common/ErrorMessage';
import apiClient from '@/api/client';

// ─── Plausibilitätsprüfung ───────────────────────────────────────────────────
//
// Arbeitsliste mit Widersprüchen: Kinder vor ihren Eltern geboren, Heiraten
// nach dem Tod, unmögliche Lebensalter. Bei einigen hundert Personen findet
// man solche Tippfehler sonst nie. Jeder Eintrag verlinkt in den Stammbaum.

type Severity = 'error' | 'warning';

interface Finding {
  code: string;
  severity: Severity;
  personId: string;
  personName: string;
  relatedId: string | null;
  relatedName: string | null;
  message: string;
}

interface PlausibilityResponse {
  totals: {
    checkedPersons: number;
    findings: number;
    errors: number;
    warnings: number;
  };
  findings: Finding[];
}

/** Sprechende Überschrift je Prüfregel – gruppiert die Liste. */
const CODE_LABELS: Record<string, string> = {
  death_before_birth: 'Sterbedatum vor Geburtsdatum',
  age_too_high: 'Unwahrscheinlich hohes Lebensalter',
  birth_in_future: 'Geburtsdatum in der Zukunft',
  death_in_future: 'Sterbedatum in der Zukunft',
  living_too_old: 'Als lebend geführt, aber sehr alt',
  living_with_death_date: 'Als lebend geführt trotz Sterbedatum',
  child_before_parent: 'Kind vor dem Elternteil geboren',
  parent_too_young: 'Elternteil zu jung',
  mother_too_old: 'Mutter bei Geburt sehr alt',
  father_too_old: 'Vater bei Geburt sehr alt',
  born_after_parent_death: 'Geboren nach dem Tod des Elternteils',
  too_many_parents: 'Mehr als zwei Eltern',
  marriage_after_death: 'Heirat nach dem Tod',
  marriage_before_birth: 'Heirat vor der Geburt',
  marriage_too_young: 'Sehr jung geheiratet',
};

function StatCard({
  label,
  value,
  tone,
  icon: Icon,
}: {
  label: string;
  value: number | string;
  tone: 'neutral' | 'red' | 'amber' | 'green';
  icon: typeof Users;
}) {
  const tones: Record<string, string> = {
    neutral: 'border-slate-200 bg-white text-slate-900',
    red: 'border-red-200 bg-red-50 text-red-700',
    amber: 'border-amber-200 bg-amber-50 text-amber-700',
    green: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  };
  return (
    <div className={`rounded-xl border p-4 ${tones[tone]}`}>
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 shrink-0 opacity-70" aria-hidden="true" />
        <span className="text-xs font-medium opacity-80">{label}</span>
      </div>
      <p className="mt-1 text-2xl font-bold tabular-nums">{value}</p>
    </div>
  );
}

export function PlausibilityPage() {
  const navigate = useNavigate();
  const [onlyErrors, setOnlyErrors] = useState(false);

  const { data, isLoading, isError, error, refetch } = useQuery<PlausibilityResponse>({
    queryKey: ['plausibility'],
    queryFn: async () => {
      const { data } = await apiClient.get<PlausibilityResponse>('/plausibility');
      return data;
    },
  });

  // Nach Prüfregel gruppieren – gleichartige Fehler lassen sich am Stück abarbeiten.
  const groups = useMemo(() => {
    const list = (data?.findings ?? []).filter((f) => !onlyErrors || f.severity === 'error');
    const map = new Map<string, Finding[]>();
    for (const f of list) {
      const arr = map.get(f.code) ?? [];
      arr.push(f);
      map.set(f.code, arr);
    }
    // Fehlergruppen zuerst, dann die größten.
    return Array.from(map.entries()).sort((a, b) => {
      const aErr = a[1][0].severity === 'error';
      const bErr = b[1][0].severity === 'error';
      if (aErr !== bErr) return aErr ? -1 : 1;
      return b[1].length - a[1].length;
    });
  }, [data, onlyErrors]);

  return (
    <AppLayout>
      <div className="h-full overflow-y-auto bg-slate-50">
        <div className="mx-auto max-w-4xl px-4 py-6 sm:px-6 sm:py-8">
          <header className="mb-6">
            <h1 className="text-xl font-bold text-slate-900 sm:text-2xl">Plausibilitätsprüfung</h1>
            <p className="mt-1 text-sm text-slate-500">
              Findet Widersprüche in Daten und Beziehungen – etwa Kinder, die vor ihren Eltern
              geboren wurden, oder Heiraten nach dem Tod.
            </p>
          </header>

          {isLoading && (
            <div className="flex justify-center py-16">
              <LoadingSpinner size="lg" message="Daten werden geprüft…" />
            </div>
          )}

          {isError && (
            <ErrorMessage
              message={error instanceof Error ? error.message : 'Prüfung fehlgeschlagen.'}
              onRetry={() => refetch()}
            />
          )}

          {data && (
            <>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <StatCard
                  label="Geprüfte Personen"
                  value={data.totals.checkedPersons}
                  tone="neutral"
                  icon={Users}
                />
                <StatCard
                  label="Fehler"
                  value={data.totals.errors}
                  tone={data.totals.errors > 0 ? 'red' : 'green'}
                  icon={ShieldAlert}
                />
                <StatCard
                  label="Hinweise"
                  value={data.totals.warnings}
                  tone={data.totals.warnings > 0 ? 'amber' : 'green'}
                  icon={AlertTriangle}
                />
              </div>

              {data.totals.findings === 0 ? (
                <div className="mt-8 rounded-xl border border-emerald-200 bg-emerald-50 p-6 text-center">
                  <CheckCircle2 className="mx-auto h-8 w-8 text-emerald-500" aria-hidden="true" />
                  <p className="mt-2 font-medium text-emerald-800">Keine Widersprüche gefunden.</p>
                  <p className="mt-1 text-sm text-emerald-700">
                    Alle geprüften Daten sind in sich stimmig.
                  </p>
                </div>
              ) : (
                <>
                  <div className="mt-6 flex items-center justify-between gap-3">
                    <h2 className="text-sm font-semibold text-slate-700">
                      {groups.reduce((n, [, list]) => n + list.length, 0)} Befunde
                    </h2>
                    <label className="flex cursor-pointer items-center gap-2 text-xs text-slate-600">
                      <input
                        type="checkbox"
                        checked={onlyErrors}
                        onChange={(e) => setOnlyErrors(e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-400"
                      />
                      Nur Fehler
                    </label>
                  </div>

                  <div className="mt-3 space-y-4">
                    {groups.map(([code, list]) => {
                      const isError = list[0].severity === 'error';
                      return (
                        <section
                          key={code}
                          className="overflow-hidden rounded-xl border border-slate-200 bg-white"
                        >
                          <header
                            className={`flex items-center gap-2 border-b px-4 py-2.5 ${
                              isError
                                ? 'border-red-100 bg-red-50/60'
                                : 'border-amber-100 bg-amber-50/60'
                            }`}
                          >
                            {isError ? (
                              <ShieldAlert className="h-4 w-4 shrink-0 text-red-500" aria-hidden="true" />
                            ) : (
                              <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" aria-hidden="true" />
                            )}
                            <h3
                              className={`text-sm font-semibold ${
                                isError ? 'text-red-800' : 'text-amber-800'
                              }`}
                            >
                              {CODE_LABELS[code] ?? code}
                            </h3>
                            <span className="ml-auto shrink-0 rounded-full bg-white/70 px-2 py-0.5 text-xs font-medium text-slate-600">
                              {list.length}
                            </span>
                          </header>

                          <ul className="divide-y divide-slate-100">
                            {list.map((f, i) => (
                              <li key={`${f.personId}-${i}`}>
                                <button
                                  onClick={() => navigate(`/?person=${f.personId}`)}
                                  className="flex w-full flex-col gap-0.5 px-4 py-3 text-left hover:bg-slate-50"
                                >
                                  <span className="flex flex-wrap items-center gap-x-2 text-sm font-medium text-slate-800">
                                    {f.personName}
                                    {f.relatedName && (
                                      <span className="text-xs font-normal text-slate-500">
                                        &middot; betrifft {f.relatedName}
                                      </span>
                                    )}
                                  </span>
                                  <span className="text-xs leading-relaxed text-slate-500">
                                    {f.message}
                                  </span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        </section>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
